import { useCallback, useEffect, useRef, useState } from "react";

type VersionPayload = { git_sha?: string; build_time?: string };

/**
 * Detects a new frontend static build via GET /version (nginx → build.json).
 * Baseline is set on mount; on every WebSocket connect (including reconnect after deploy)
 * we fetch again and compare git_sha only.
 */
export function useVersionCheck() {
  const baselineShaRef = useRef<string | null>(null);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const res = await fetch("/version", { cache: "no-store" });
      if (!res.ok) return;
      const data = (await res.json()) as VersionPayload;
      const sha = data.git_sha;
      if (typeof sha !== "string") return;

      if (baselineShaRef.current === null) {
        baselineShaRef.current = sha;
        return;
      }
      if (sha !== baselineShaRef.current) {
        setUpdateAvailable(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    void checkVersion();
  }, [checkVersion]);

  useEffect(() => {
    let cancelled = false;
    let ws: typeof import("../services/websocketService").websocketService | null = null;
    const onConnect = () => {
      void checkVersion();
    };

    import("../services/websocketService")
      .then((mod) => {
        if (cancelled) return;
        ws = mod.websocketService;
        ws.on("connect", onConnect);
      })
      .catch(() => {
        /* ignore */
      });

    return () => {
      cancelled = true;
      if (ws) {
        ws.off("connect", onConnect);
      }
    };
  }, [checkVersion]);

  const dismiss = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  return { updateAvailable, dismiss };
}
